import { NumberFormatFields } from '@euler/app/components/message/widgets/bi-visualization/number-format/NumberFormatFields';
import { biFormatSummary } from '@euler/app/components/message/widgets/bi-visualization/number-format/functions/biFormatSummary';
import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDisplayFormat } from '@euler/model/bi/format';
import { ChevronDown, ChevronRight } from 'lucide-react-native';
import { memo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { BiEditorFormatTarget } from '../functions/biEditorFormats';

/** One measure's format: a summary line that opens its options. */
export const FormatColumnRow = memo(
  ({
    target,
    onChange,
  }: {
    target: BiEditorFormatTarget;
    /** Null goes back to the column's own format. */
    onChange: (format: BiDisplayFormat | null) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [open, setOpen] = useState(false);
    const Icon = open ? ChevronDown : ChevronRight;
    return (
      <View
        style={{
          borderRadius: rpx(10),
          borderWidth: 1,
          borderColor: aiColors.border,
          overflow: 'hidden',
        }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${target.name} 数字格式`}
          accessibilityState={{ expanded: open }}
          onPress={() => setOpen(value => !value)}
          style={({ pressed }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: rpx(8),
            paddingHorizontal: rpx(12),
            paddingVertical: rpx(10),
            backgroundColor: pressed ? aiColors.muted : aiColors.card,
          })}
        >
          <Typography.Text
            medium
            size={rpx(13)}
            color={aiColors.foreground}
            numberOfLines={1}
            style={{ flexShrink: 1 }}
          >
            {target.name}
          </Typography.Text>
          <Typography.Text
            size={rpx(12)}
            color={target.own ? aiColors.primary : aiColors.mutedForeground}
            numberOfLines={1}
            style={{ flex: 1, textAlign: 'right' }}
          >
            {biFormatSummary(target.current)}
          </Typography.Text>
          <Icon size={15} color={aiColors.mutedForeground} />
        </Pressable>
        {open ? (
          <View
            style={{
              gap: rpx(10),
              padding: rpx(12),
              backgroundColor: aiColors.muted,
            }}
          >
            <NumberFormatFields
              format={target.current}
              kinds={target.kinds}
              sample={target.sample}
              onChange={onChange}
            />
            {target.own ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => onChange(null)}
              >
                <Typography.Text size={rpx(12)} color={aiColors.primary}>
                  恢复默认（{biFormatSummary(target.base)}）
                </Typography.Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </View>
    );
  },
);
